'use client'

import { useRef } from 'react'

import { ChevronDownIcon, XIcon } from 'lucide-react'

import { Button } from '~/components/component-library/Button'
import { type FilterTriggerProps } from '~/components/component-library/FilterTrigger/types'
import { PopoverTrigger } from '~/components/ui/popover'
import { cn } from '~/utils/tailwind-utils'

export { type FilterTriggerProps } from '~/components/component-library/FilterTrigger/types'

/**
 * A filter's open control with a separate clear control beside it. Render it
 * inside a `Popover`; the caller owns the `Popover` and its `PopoverContent`.
 */
export const FilterTrigger = ({
  label,
  appliedLabel,
  isApplied,
  onClear,
  clearLabel,
  prefix,
  size = 'base',
  isDisabled = false,
  onOpenKeyDown,
}: FilterTriggerProps) => {
  const openRef = useRef<HTMLButtonElement>(null)

  const clear = () => {
    onClear()
    // The clear control unmounts once cleared, so focus would otherwise fall to the body
    openRef.current?.focus()
  }

  return (
    <div className="inline-flex min-w-0 max-w-full">
      <PopoverTrigger
        asChild
        onKeyDown={(event) => {
          if (event.key === 'Backspace' && isApplied) {
            event.preventDefault()
            clear()
          }
          onOpenKeyDown?.(event)
        }}
      >
        <Button
          ref={openRef}
          type="button"
          variant="secondary"
          size={size}
          isSelected={isApplied}
          isDisabled={isDisabled}
          prefix={prefix}
          suffix={isApplied ? undefined : <ChevronDownIcon />}
          className={cn('min-w-0', { 'rounded-r-none': isApplied })}
        >
          {isApplied && appliedLabel ? (
            <span className="max-w-48 truncate">
              {/* Keeps the filter name in the accessible name: "Network: Optimism" */}
              <span className="sr-only">{`${label}:`}</span> {appliedLabel}
            </span>
          ) : (
            label
          )}
        </Button>
      </PopoverTrigger>
      {isApplied && (
        <Button
          type="button"
          variant="secondary"
          size={size}
          isSelected
          isDisabled={isDisabled}
          prefix={<XIcon />}
          aria-label={clearLabel ?? `Clear ${label} filter`}
          onClick={clear}
          className="-ml-px shrink-0 rounded-l-none"
        />
      )}
    </div>
  )
}
